import { z } from 'zod';
import { ArtefactKindSchema } from './enums';
import { ArtefactIdSchema } from './ids';

/**
 * How much of each source artefact the pipeline was actually able to read
 * (docs/DECISIONS.md ZD-025).
 *
 * Its own module because both layers carry it: the extraction records what it
 * could see, and the canonical model lifts that record rather than re-deriving
 * it from storage (ZD-014). Putting it in `./canonical` would have made the
 * pre-canonical layer import the canonical one, which is backwards.
 */

/**
 * How much of an artefact the pipeline was actually able to read
 * (docs/DECISIONS.md ZD-025).
 *
 * ZD-018 handled the artefact that is entirely a scan. This handles the one
 * that is partly a scan, which is worse, because it looks like a success: the
 * readable pages index, the unreadable ones are silently omitted, and an
 * extraction can appear complete while missing half its source.
 *
 * A typed record rather than a note in an unknown, because **a report needs a
 * denominator and so does a metric.** "Pages 3-5 could not be read" is a
 * sentence; "3 of 9 pages readable" is a measure, and documentation coverage is
 * only a chargeable finding rather than an excuse if it can be measured.
 */
const ArtefactCoverageShape = z.object({
  artefact_id: ArtefactIdSchema,
  kind: ArtefactKindSchema,
  /** Pages the artefact has. Null for a format that is not paginated. */
  page_count: z.int().positive().nullable(),
  /** Pages that produced at least one citable unit. */
  readable_pages: z.array(z.int().positive()),
  /** Pages that produced none — a scan inside a document with a text layer. */
  unreadable_pages: z.array(z.int().positive()),
  /** Citable units produced in total, across every format. */
  unit_count: z.int().nonnegative(),
});

export const ArtefactCoverageSchema = ArtefactCoverageShape.superRefine((coverage, ctx) => {
  const { page_count, readable_pages, unreadable_pages } = coverage;

  if (page_count === null) {
    // A text or JSON artefact has no pages, so claiming pages either way would
    // be inventing structure the format does not have.
    if (readable_pages.length > 0 || unreadable_pages.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'an artefact with no page count cannot list readable or unreadable pages',
        path: ['page_count'],
      });
    }
    return;
  }

  // Every page is accounted for, exactly once. This is the invariant that makes
  // the record a measure: if pages could go missing from both lists, "3 of 9
  // readable" would carry the same silent gap the record exists to close.
  const seen = new Set([...readable_pages, ...unreadable_pages]);
  if (seen.size !== readable_pages.length + unreadable_pages.length) {
    ctx.addIssue({
      code: 'custom',
      message: 'a page cannot be listed twice, or as both readable and unreadable',
      path: ['unreadable_pages'],
    });
  }
  if (seen.size !== page_count) {
    ctx.addIssue({
      code: 'custom',
      message: `every page must be accounted for: ${String(seen.size)} listed against a page count of ${String(page_count)}`,
      path: ['unreadable_pages'],
    });
  }
  for (const page of seen) {
    if (page > page_count) {
      ctx.addIssue({
        code: 'custom',
        message: `page ${String(page)} is beyond the artefact's ${String(page_count)} pages`,
        path: ['unreadable_pages'],
      });
    }
  }
});
export type ArtefactCoverage = z.infer<typeof ArtefactCoverageShape>;

/** The share of an artefact's pages that produced something citable, 0..1. */
export function coverageRatio(coverage: ArtefactCoverage): number {
  if (coverage.page_count === null) return coverage.unit_count > 0 ? 1 : 0;
  if (coverage.page_count === 0) return 0;
  return coverage.readable_pages.length / coverage.page_count;
}

/** True when nothing in the artefact was skipped. */
export function isFullyRead(coverage: ArtefactCoverage): boolean {
  return coverage.unreadable_pages.length === 0 && coverage.unit_count > 0;
}
